import React, { useState } from 'react';
import { signInWithPopup, AuthProvider } from 'firebase/auth';
import { auth, googleProvider, appleProvider } from '../firebase';

const LoginPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // iPhone アプリは Apple サインイン、Android アプリは Google サインイン。
  // uid が別なので、アプリで使ったのと同じ方法でログインしないと議事録が出ない。
  const handleSignIn = async (provider: AuthProvider) => {
    setLoading(true);
    setError(null);
    try {
      await signInWithPopup(auth, provider);
    } catch (e: any) {
      // ポップアップを自分で閉じただけのケースはエラー表示しない。
      if (e?.code === 'auth/popup-closed-by-user' || e?.code === 'auth/cancelled-popup-request') {
        setLoading(false);
        return;
      }
      setError(e.message || 'ログインに失敗しました');
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <img
          src={`${process.env.PUBLIC_URL}/logo192.png`}
          alt="VoiLog"
          style={styles.icon}
        />
        <h1 style={styles.title}>VoiLog</h1>
        <p style={styles.subtitle}>会議の議事録を管理・閲覧できます</p>

        {error && <div style={styles.error}>{error}</div>}

        <button
          style={{ ...styles.appleButton, opacity: loading ? 0.7 : 1 }}
          onClick={() => handleSignIn(appleProvider)}
          disabled={loading}
        >
          <svg style={styles.appleIcon} viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#fff" d="M16.36 12.72c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.61-1.7-3.18-1.72-1.35-.14-2.64.79-3.33.79-.69 0-1.74-.77-2.86-.75-1.47.02-2.83.85-3.59 2.16-1.53 2.65-.39 6.57 1.1 8.72.73 1.05 1.6 2.23 2.75 2.19 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.71.71 2.87.69 1.19-.02 1.94-1.07 2.66-2.13.84-1.22 1.19-2.4 1.21-2.46-.03-.01-2.32-.89-2.34-3.53zM14.2 6.2c.61-.74 1.02-1.77.91-2.8-.88.04-1.94.59-2.57 1.32-.56.65-1.05 1.7-.92 2.7.98.08 1.98-.5 2.58-1.22z"/>
          </svg>
          {loading ? 'ログイン中...' : 'Appleでログイン'}
        </button>

        <button
          style={{ ...styles.googleButton, opacity: loading ? 0.7 : 1 }}
          onClick={() => handleSignIn(googleProvider)}
          disabled={loading}
        >
          <svg style={styles.googleIcon} viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {loading ? 'ログイン中...' : 'Googleでログイン'}
        </button>

        <p style={styles.note}>
          アプリで使っているのと同じ方法でログインしてください。
          iPhone は Apple、Android は Google です。
        </p>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '48px 40px',
    textAlign: 'center',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    maxWidth: '400px',
    width: '100%',
  },
  icon: {
    width: '72px',
    height: '72px',
    borderRadius: '16px',
    marginBottom: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '15px',
    color: '#666',
    margin: '0 0 32px 0',
  },
  error: {
    background: '#fce8e6',
    color: '#c5221f',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'left',
  },
  googleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px 24px',
    background: 'white',
    border: '1px solid #dadce0',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 500,
    color: '#3c4043',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s',
    boxSizing: 'border-box',
  },
  googleIcon: {
    width: '20px',
    height: '20px',
  },
  // Apple HIG 準拠の黒ボタン（アプリ側 login_screen.dart と揃える）
  appleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px 24px',
    background: '#000',
    border: '1px solid #000',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 500,
    color: '#fff',
    cursor: 'pointer',
    boxSizing: 'border-box',
    marginBottom: '12px',
  },
  appleIcon: {
    width: '20px',
    height: '20px',
  },
  note: {
    fontSize: '12px',
    color: '#888',
    margin: '16px 0 0 0',
    lineHeight: 1.6,
  },
};

export default LoginPage;
